import { RealtimeSetupError, UnknownTriggerError } from '../../errors'
import type { ExecutionContext } from '../context/execution-context'
import type { Extension } from '../extension'
import { z } from '../params'
import { getPath } from '../path'
import type { SystemMethodSpec } from '../system-method'
import type { AnySchema, ProviderPayload } from '../types'
import { REALTIME_SYSTEM_METHODS } from './method-names'
import {
  RealtimeDeleteInvocationSchema,
  RealtimeInboundInvocationSchema,
  RealtimeSelectInvocationSchema,
  RealtimeSubscriptionInvocationSchema,
} from './system-params'
import type { RealtimeInvocation, RealtimeSetupProps, RealtimeSubTrigger } from './types'
import { verifyDeclarative } from './verify'

// `ext.setupRealtimeTriggers(...)` — the webhook plumbing shared by every realtime trigger of a service
// (09-realtime-triggers.md). The author writes declarative config plus lifecycle functions; this owns the
// five v1 SYSTEM methods and drives them:
//
//   handleTriggerUpsertWebhook  → subscribe                    register the callback URL with the provider
//   handleTriggerDeleteWebhook  → unsubscribe                  tear it down
//   handleTriggerRefreshWebhook → refresh (optional)           renew a subscription that expires
//   handleTriggerResolveEvents  → handshake → verify → route   shape one inbound delivery into events
//   handleTriggerSelectMatched  → per-instance match           pick which configured instances fire
//
// Splitting resolve from select is what keeps the expensive work rare: resolve runs once per delivery and
// touches no API, select runs per event and may enrich once, only if something actually matched.

// Read a `string | fn` extractor against the request body.
function readExtract(
  extract: string | ((bag: { requestBody: ProviderPayload }) => unknown) | undefined,
  body: unknown
): unknown {
  if (typeof extract === 'function') {
    return extract({ requestBody: body })
  }

  if (typeof extract === 'string') {
    return getPath(body, extract)
  }

  return undefined
}

function isEmptyBody(body: unknown): boolean {
  return body == null || (typeof body === 'object' && Object.keys(body as object).length === 0)
}

export class RealtimeSetup {
  constructor(private readonly extension: Extension, private readonly props: RealtimeSetupProps) {
    if (typeof props.subscribe !== 'function' || typeof props.unsubscribe !== 'function') {
      throw new RealtimeSetupError(extension.id, 'setupRealtimeTriggers requires subscribe and unsubscribe functions')
    }

    this.#registerSystemMethods()
  }

  get scope(): RealtimeSetupProps['scope'] {
    return this.props.scope
  }

  get hasRefresh(): boolean {
    return typeof this.props.refresh === 'function'
  }

  // ── SYSTEM seam: name · input · handler, in one table ──────────────────────

  #registerSystemMethods(): void {
    const M = REALTIME_SYSTEM_METHODS

    // Every realtime SYSTEM method takes the same single `invocation` param; only its shape differs.
    const spec = (
      name: string,
      schema: AnySchema,
      run: (invocation: RealtimeInvocation, ctx: ExecutionContext) => unknown,
      published = true
    ): SystemMethodSpec => ({
      name,
      scheme: z.object({ invocation: schema }),
      published,
      run: ({ invocation }, ctx) => run(invocation as RealtimeInvocation, ctx),
    })

    this.extension.registerSystemMethods([
      spec(M.UPSERT, RealtimeSubscriptionInvocationSchema, (i, ctx) => this.#subscribe(i, ctx)),
      spec(M.DELETE, RealtimeDeleteInvocationSchema, (i, ctx) => this.#unsubscribe(i, ctx)),
      spec(M.RESOLVE, RealtimeInboundInvocationSchema, (i, ctx) => this.#resolve(i, ctx)),
      spec(M.SELECT, RealtimeSelectInvocationSchema, (i, ctx) => this.#selectMatched(i, ctx)),
      // Refresh stays CALLABLE either way — it answers `{}` when no handler was declared — but is only
      // advertised in the model when the author actually declared one.
      spec(M.REFRESH, RealtimeSubscriptionInvocationSchema, (i, ctx) => this.#refresh(i, ctx), this.hasRefresh),
    ])
  }

  // ── subscription lifecycle ─────────────────────────────────────────────────

  async #subscribe(invocation: RealtimeInvocation, ctx: ExecutionContext): Promise<object> {
    const result = await this.props.subscribe(ctx.bagWith({
      callbackUrl: invocation.callbackUrl as string,
      triggers: this.#toSubTriggers(invocation.events),
      webhookData: invocation.webhookData ?? null,
      connectionId: invocation.connectionId,
    }))

    return {
      webhookData: result?.webhookData,
      eventScopeId: result?.eventScopeId,
      refreshIntervalInSeconds: result?.refreshIntervalInSeconds,
    }
  }

  async #unsubscribe(invocation: RealtimeInvocation, ctx: ExecutionContext): Promise<object> {
    const result = await this.props.unsubscribe(ctx.bagWith({
      callbackUrl: invocation.callbackUrl,
      webhookData: invocation.webhookData,
      connectionId: invocation.connectionId,
    }))

    return { webhookData: result?.webhookData ?? {} }
  }

  async #refresh(invocation: RealtimeInvocation, ctx: ExecutionContext): Promise<object> {
    if (!this.props.refresh) {
      return {}
    }

    const result = await this.props.refresh(ctx.bagWith({
      callbackUrl: invocation.callbackUrl as string,
      triggers: this.#toSubTriggers(invocation.events),
      webhookData: invocation.webhookData,
    }))

    return { webhookData: result?.webhookData, refreshIntervalInSeconds: result?.refreshIntervalInSeconds }
  }

  // v1 upsert/refresh hand over events [{ name, triggerData }]. A trigger that declares provider event
  // names expands to one entry EACH, which is what a provider's subscribe API actually wants.
  //
  // A trigger that declares NONE still gets an entry. `providerEventName` is a ROUTING declaration and this
  // is a LIFECYCLE seam: an extension that resolves its own events (`resolveEvents`) legitimately has nothing to
  // route by, yet its triggers still run and `subscribe`/`refresh` still have to reconcile them. Gating the
  // bag on the routing declaration handed such a service an empty bag on every subscribe — silently, since
  // an empty subscription is a perfectly well-formed result.
  //
  // NOTHING is dropped here. A `name` resolving to no trigger THROWS, because this is the CONTROL PLANE:
  // upsert/refresh are initiated by FlowRunner itself, something is waiting on the result, and no third
  // party is affected — so an error reaches a place where it can be acted on. `subscribe` is the seam that
  // registers with the PROVIDER, so a dropped entry meant a flow that reported LIVE while its trigger was
  // never subscribed — silent, and indistinguishable from a working flow that simply never fired. One throw
  // does block the rest of the batch from subscribing, and that is the intent: a flow refusing to start
  // beats a live flow with half its triggers registered. (The data plane is ruled the other way — see
  // `#selectMatched`, which declines rather than throws because nothing is waiting on it.)
  #toSubTriggers(events: RealtimeInvocation['events'] = []): RealtimeSubTrigger[] {
    const subTriggers: RealtimeSubTrigger[] = []

    for (const event of events) {
      const trigger = this.extension.findRealtimeTrigger(event.name)

      if (!trigger) {
        throw new UnknownTriggerError(this.extension.id, event.name, 'realtime')
      }

      const params = event.triggerData ?? {}
      const providerEventNames = trigger.providerEventNames(params)

      // No placeholder: a `providerEventName` the provider has never heard of would reach `subscribe`, the
      // seam that calls the provider's own subscription API. A missing field an author can test for is safer.
      if (!providerEventNames.length) {
        subTriggers.push({ id: event.name, params })
        continue
      }

      for (const providerEventName of providerEventNames) {
        subTriggers.push({ id: event.name, params, providerEventName })
      }
    }

    return subTriggers
  }

  // ── inbound delivery ───────────────────────────────────────────────────────

  async #resolve(invocation: RealtimeInvocation, ctx: ExecutionContext): Promise<object> {
    const headers = invocation.headers ?? {}
    const body = invocation.body
    const rawBody = invocation.rawBody

    // 1. Endpoint-verification handshake (challenge/echo). Anything returned → ack without processing.
    if (this.props.handshake) {
      const result = await this.props.handshake(ctx.bagWith({ requestBody: body, headers, rawBody }))

      if (result && (
        result.responseToExternalService !== undefined || result.responseHeaders || result.responseToWebhook
      )) {
        // Both shapes travel, exactly as the author wrote them. `responseToWebhook` is the newer, fuller
        // one, but the platform does not understand it yet — so translating the older pair into it would
        // drop the response of every service already using it. The platform picks whichever it supports.
        return {
          handshake: true,
          responseToExternalService: result.responseToExternalService,
          responseHeaders: result.responseHeaders,
          responseToWebhook: result.responseToWebhook,
        }
      }
    }

    // Empty-body deliveries are auto-acked.
    if (isEmptyBody(body)) {
      return { handshake: true, events: [] }
    }

    // 2. Signature verification (escape hatch or declarative). Reject on failure — by RETURNING a 401,
    // never by throwing.
    //
    // A forged delivery is a normal, expected outcome of a public callback URL, not a runtime failure. A
    // throw leaves the pod with a 4xx, which the platform reads as "the invocation failed" rather than "the
    // extension answered 401" — so the provider receives a 500. That fails exactly the validation this is
    // for: Xero's Intent to Receive sends a deliberately mis-signed delivery and requires a 401 back.
    //
    // Empty `rawBody` rather than `body`: a rejection carries no content, and `body` is always JSON, so an
    // empty string there would arrive quoted.
    if (!await this.#verify(rawBody, headers, ctx)) {
      ctx.logger.warn(`[flow-extension:${ this.extension.id }] webhook signature verification failed — answering 401`)

      return { events: [], responseToWebhook: { httpStatus: 401, rawBody: '' } }
    }

    // 3. Escape hatch for irregular/multi-shape payloads — emits v1 events directly.
    if (this.props.resolveEvents) {
      const result = await this.props.resolveEvents(ctx.bagWith({
        rawBody,
        headers,
        query: invocation.queryParams ?? {},
        body,
        connectionId: invocation.connectionId,
      }))

      return {
        handshake: result.handshake,
        responseToExternalService: result.responseToExternalService,
        responseHeaders: result.responseHeaders,
        responseToWebhook: result.responseToWebhook,
        eventScopeId: result.eventScopeId,
        events: (result.events ?? []).map(event => ({ name: event.id, data: event.providerEventData })),
      }
    }

    // 4. Declarative routing: discriminator → every trigger claiming it; build providerEventData.
    const eventName = readExtract(this.props.extractProviderEventName, body)

    if (eventName == null) {
      return { events: [] }
    }

    const baseData = this.props.extractProviderEventData
      ? readExtract(this.props.extractProviderEventData, body)
      : body
    const events: { name: string; data: unknown }[] = []

    for (const trigger of this.extension.realtimeTriggers()) {
      if (!trigger.claims(eventName as string)) {
        continue
      }

      const extra = trigger.extendEventData(body, baseData as Record<string, unknown>)

      events.push({ name: trigger.id, data: extra ? { ...(baseData as object), ...extra } : baseData })
    }

    const eventScopeId = this.props.scopeAt ? getPath(body, this.props.scopeAt) : undefined

    return { events, eventScopeId, connectionId: invocation.connectionId }
  }

  // The author's own check wins; otherwise the declarative one; otherwise nothing was asked for.
  async #verify(rawBody: string | undefined, headers: Record<string, unknown>, ctx: ExecutionContext): Promise<boolean> {
    if (this.props.verifyRequest) {
      return !!await this.props.verifyRequest(ctx.bagWith({ rawBody, headers, config: ctx.config }))
    }

    if (this.props.verify) {
      return verifyDeclarative(this.props.verify, rawBody, headers, ctx.config)
    }

    return true
  }

  // Fan one resolved event out over the configured instances of its trigger. An `eventName` this service
  // does not declare is DECLINED outright — `{ ids: [], enrichedEventData: null }`, no instance offered up
  // and no per-event seam run. That is deliberately not the same case as a trigger which declares no
  // `match`: `matches()` already answers "fires always" for that one, so resolve the name FIRST and never
  // let the two share a branch.
  //
  // Declining is the GUARANTEE here, not an accident of a past fix, and it is why this seam is ruled the
  // opposite way to `#toSubTriggers`. This is the DATA PLANE: the platform has already answered the
  // provider and detached this work, so a throw reaches nobody — and worse, it aborts every remaining event
  // of the same delivery, dropping the good ones beside the stale name.
  async #selectMatched(invocation: RealtimeInvocation, ctx: ExecutionContext): Promise<object> {
    // `eventName` is in practice always present — RealtimeSelectInvocationSchema declares it
    // `z.string().min(1)`, so SystemMethod.validate rejects a missing one before this runs. The shared
    // invocation bag cannot say so (the other four SYSTEM methods carry no event name), and an absent name
    // is not a trigger id either way, so it folds into the same decline rather than earning a branch.
    const trigger = invocation.eventName ? this.extension.findRealtimeTrigger(invocation.eventName) : undefined

    if (!trigger) {
      return { ids: [], enrichedEventData: null }
    }

    const eventData = invocation.eventData
    const ids = (invocation.triggers ?? [])
      .filter(instance => trigger.matches(instance.data ?? {}, eventData))
      .map(instance => instance.id)

    // extendResult runs ONCE if any instance matched — the only per-event place that may call the API.
    const enrichedEventData = ids.length && trigger.hasExtendResult()
      ? await trigger.extendResult(eventData, ctx)
      : null

    return { ids, enrichedEventData }
  }
}
